import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Resolves the current user's team_members.id within the given org.
 *
 * Cleaners are linked to their team_members row by `user_id`, but the dashboard
 * data hook only loads `id, name` and can't tell which row is the logged-in
 * cleaner. This is the minimal, RLS-safe lookup needed to identify the cleaner's
 * own jobs (a cleaner can always read their own team_members row). Enabled only
 * for cleaners so owner/super_admin dashboards never fire it.
 */
export interface CleanerTeamMemberRef {
  id: string;
  organizationId: string;
}

export function useCleanerTeamMember(
  orgId: string | null | undefined,
  enabled: boolean,
) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['cleaner-team-member-ref', orgId, user?.id],
    enabled: Boolean(user?.id) && enabled,
    queryFn: async (): Promise<CleanerTeamMemberRef | null> => {
      if (orgId) {
        const { data: orgScoped } = await supabase
          .from('team_members')
          .select('id, organization_id')
          .eq('organization_id', orgId)
          .eq('user_id', user!.id)
          .maybeSingle();

        if (orgScoped?.id && orgScoped.organization_id) {
          return { id: orgScoped.id, organizationId: orgScoped.organization_id };
        }
      }

      const { data: ownRows } = await supabase
        .from('team_members')
        .select('id, organization_id, created_at')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const fallback = ownRows?.[0];
      return fallback?.id && fallback.organization_id
        ? { id: fallback.id, organizationId: fallback.organization_id }
        : null;
    },
  });
}

export function useCleanerTeamMemberId(
  orgId: string | null | undefined,
  enabled: boolean,
) {
  const cleanerTeamMember = useCleanerTeamMember(orgId, enabled);

  return {
    ...cleanerTeamMember,
    data: cleanerTeamMember.data?.id ?? null,
  };
}
